<template>
  <a-card
      :bordered="true"
      class="demographic-role-distribution-card"
  >
    <div class="demographic-role-distribution-card__header">
      <div>
        <h3 class="demographic-role-distribution-card__title">
          {{ title }}
        </h3>

        <p
            v-if="description"
            class="demographic-role-distribution-card__description"
        >
          {{ description }}
        </p>
      </div>

      <QuestionCircleOutlined class="demographic-role-distribution-card__help-icon" />
    </div>

    <div class="demographic-role-distribution-card__legend">
      <div
          v-for="item in normalizedLegendItems"
          :key="item.key"
          class="demographic-role-distribution-card__legend-item"
      >
        <span
            class="demographic-role-distribution-card__legend-mark"
            :style="{ backgroundColor: item.color }"
        ></span>

        <span>{{ item.label }}</span>
      </div>
    </div>

    <div class="demographic-role-distribution-card__groups">
      <div
          v-for="group in normalizedGroups"
          :key="group.key"
          class="demographic-role-distribution-card__group"
      >
        <div class="demographic-role-distribution-card__group-title">
          {{ group.label }}
        </div>

        <div
            class="demographic-role-distribution-card__bar"
            :style="group.barStyle"
        >
          <div
              v-for="item in group.items"
              :key="item.key"
              class="demographic-role-distribution-card__bar-segment"
              :style="{ backgroundColor: item.segmentColor }"
          ></div>
        </div>

        <div class="demographic-role-distribution-card__values">
          <span
              v-for="item in group.items"
              :key="item.key"
              class="demographic-role-distribution-card__value"
              :style="{ left: item.center + '%' }"
          >
            {{ formatValue(item.value) }}
          </span>
        </div>
      </div>
    </div>
  </a-card>
</template>

<script>
import { computed } from 'vue'
import { QuestionCircleOutlined } from '@ant-design/icons-vue'

const DEFAULT_COLORS = {
  drivers: '#A29BFE',
  strategists: '#71E5E0',
  balanced: '#D5DBE2',
}

const LOCKED_BAR_COLOR = '#D1D1D1'

export default {
  name: 'DemographicRoleDistributionCard',

  components: {
    QuestionCircleOutlined,
  },

  props: {
    title: {
      type: String,
      default: 'Распределение ролей',
    },

    description: {
      type: String,
      default: '',
    },

    legendItems: {
      type: Array,
      default: () => [],
    },

    groups: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    const normalizedLegendItems = computed(() => {
      return (Array.isArray(props.legendItems) ? props.legendItems : []).map((item, index) => ({
        key: item.key || `legend-${index}`,
        label: item.label || '',
        color: item.color || DEFAULT_COLORS[item.key] || '#D5DBE2',
      }))
    })

    const normalizedGroups = computed(() => {
      return (Array.isArray(props.groups) ? props.groups : []).map((group, groupIndex) => {
        const rawItems = Array.isArray(group.items) ? group.items : []

        const fallbackItems = normalizedLegendItems.value.map((item) => ({
          key: item.key,
          label: item.label,
          color: item.color,
          value: 0,
        }))

        const sourceItems = rawItems.length ? rawItems : fallbackItems

        const preparedItems = sourceItems.map((item, itemIndex) => ({
          ...item,
          key: item.key || `item-${itemIndex}`,
          label: item.label || '',
          value: toNumber(item.value),
          color: item.color || DEFAULT_COLORS[item.key] || '#D5DBE2',
        }))

        const total = preparedItems.reduce((sum, item) => sum + item.value, 0)
        const hasData = total > 0

        const visibleItems = hasData
            ? preparedItems.filter((item) => item.value > 0)
            : fallbackItems.map((item, itemIndex) => ({
              ...item,
              key: item.key || `locked-item-${itemIndex}`,
              value: 0,
              color: item.color || DEFAULT_COLORS[item.key] || '#D5DBE2',
            }))

        let offset = 0

        const items = visibleItems.map((item, itemIndex) => {
          const value = toNumber(item.value)
          const width = hasData ? (value / total) * 100 : 100 / visibleItems.length
          const center = offset + width / 2

          offset += width

          return {
            ...item,
            key: item.key || `item-${itemIndex}`,
            value,
            width,
            center,
            ratio: hasData ? value : 1,
            segmentColor: hasData ? item.color : LOCKED_BAR_COLOR,
          }
        })

        return {
          key: group.key || `group-${groupIndex}`,
          label: group.label || '',
          items,
          barStyle: {
            gridTemplateColumns: items.map((item) => `${item.ratio}fr`).join(' '),
          },
        }
      })
    })

    const formatValue = (value) => {
      const number = toNumber(value)

      return Number.isInteger(number)
          ? number
          : number.toFixed(1)
    }

    return {
      normalizedLegendItems,
      normalizedGroups,
      formatValue,
    }
  },
}

function toNumber(value) {
  const number = Number(value)

  return Number.isFinite(number) ? number : 0
}
</script>

<style scoped lang="scss">
.demographic-role-distribution-card {
  min-width: 0;
  height: 100%;
  border-radius: 8px;
}

.demographic-role-distribution-card :deep(.ant-card-body) {
  height: 100%;
}

.demographic-role-distribution-card__header {
  min-width: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
}

.demographic-role-distribution-card__title {
  margin: 0 0 8px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.demographic-role-distribution-card__description {
  margin: 0;
  font-size: 14px;
  line-height: 130%;
  color: rgba(0, 0, 0, 0.45);
}

.demographic-role-distribution-card__help-icon {
  flex: 0 0 auto;
  margin-top: 2px;
  color: rgba(0, 0, 0, 0.25);
  font-size: 14px;
}

.demographic-role-distribution-card__legend {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 20px;
}

.demographic-role-distribution-card__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.65);
}

.demographic-role-distribution-card__legend-mark {
  width: 10px;
  height: 10px;
  flex: 0 0 auto;
  border-radius: 2px;
}

.demographic-role-distribution-card__groups {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.demographic-role-distribution-card__group {
  min-width: 0;
}

.demographic-role-distribution-card__group-title {
  margin-bottom: 8px;
  font-weight: 600;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.demographic-role-distribution-card__bar {
  width: 100%;
  height: 8px;
  display: grid;
  gap: 2px;
  overflow: hidden;
  border-radius: 999px;
  background: #eef1f5;
}

.demographic-role-distribution-card__bar-segment {
  height: 100%;
  min-width: 0;
}

.demographic-role-distribution-card__values {
  position: relative;
  height: 16px;
  margin-top: 4px;
}

.demographic-role-distribution-card__value {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  font-size: 12px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.35);
  white-space: nowrap;
}

@media (max-width: $mobile-breakpoint) {
  .demographic-role-distribution-card :deep(.ant-card-body) {
    padding: 14px 12px;
  }
}
</style>